'use client';
import ExternalLink from "@/app/assets/icons/external-arrow";
import BiddRedirectPopup from "@/app/components/shared/bidd-redirect-popup";
import { BondData } from "@/app/interface/bonds";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
// import { initBranch, trackEvent } from "@/app/utils/branchio";
import { formatIndianCurrency, trimText } from "@/app/utils/helper";
import { Progress } from "@/components/ui/progress";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import BiddLogo from "@/app/assets/icons/bidd-logo";
import moment from "moment";
import React, { useState } from "react";
import useMobile from "@/app/hooks/use-mobile";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";

interface BondProductCardsProps {
    product?: BondData;
}

const BondProductCard: React.FC<BondProductCardsProps> = ({ product }) => {
  const [popupRedirectToBidd, setPopupRedirectToBidd] = useState<boolean>(false);
  const [currentData, setCurrentData] = useState({});
  const { isMobile } = useMobile();
  const { cleverTapTrackEvent } = useCleverTapEvents();

    const toggleBiddRedirectPopup = () => {
        setPopupRedirectToBidd(!popupRedirectToBidd);
        setCurrentData({});
    }

    const getMldReturns = (min: number, max: number) => {
        if (min === 0) {
          return `Up to ${max}%`;
        } else if (min > 0) {
          return `${min}% - ${max}%`;
        }
    }

    const redirectToProduct = (product:string, productType: string) => {
        setAnalyticsEvent(window.location.href, `CB_Listing_Page_View_Details_${product}`);
        cleverTapTrackEvent(`CB_Listing_Page_View_Details_${product}`, {});
        // initBranch().then(() => {
        //   trackEvent(`CB_Listing_Page_View_Details_${product}`);
        // }).catch(error => console.error('Error initializing Branch:', error));

        window.location.href = `${process.env.ICM_BONDS_URL}/platform/#/incredmoney-asset/${productType}/${product}`;
    };

    const handleBiddRedirect = (productType:string, product: string, redirectUrl: string) => {
        setCurrentData({
            product: product,
            productType: productType,
            redirectUrl: redirectUrl
        });
        setAnalyticsEvent(window.location.href, `CB_Listing_Page_View_Details_${product}`);
        cleverTapTrackEvent(`CB_Listing_Page_View_Details_${product}`, {});
        // initBranch().then(() => {
        //   trackEvent(`CB_Listing_Page_View_Details_${product}`);
        // }).catch(error => console.error('Error initializing Branch:', error));
        setPopupRedirectToBidd(true);
    }

    return (
        <div
            className="flex h-auto w-full flex-col rounded-xl border border-neutralOpacityDark-20 p-s9"
            id="bonds-product-card-inner"
        >
            <div className="mb-6 flex flex-col items-center gap-4 border-b border-dashed border-neutralOpacityDark-20 pb-s9">
                <div className="relative flex w-full items-center justify-start gap-4">
                    {/* <div className="flex !h-[62px] !w-[62px] items-center justify-center rounded-[12px] border border-neutralOpacityDark-20"> */}
                    <img
                        src={product?.logoUrl}
                        alt={`${product?.displayName} logo`}
                        className="h-[62px] w-[62px] rounded-[12px] border border-neutralOpacityDark-20"
                    />
                    {/* </div> */}
                    <div className="flex w-[140px] sm:w-[190px] h-[62px] flex-col items-start justify-center">
                        <p className="mb-[2px] w-full text-left text-lg font-semibold leading-[20px]" title={product?.displayName ?? ""}>
                            {trimText(product?.displayName ?? "", isMobile ? 24 : 40)}
                        </p>
                        <p className="text-left text-xs font-semibold capitalize leading-[19.2px] tracking-[0.12px] text-brand-primary-500 opacity-95 mt-1">
                            {product?.productType}
                        </p>
                    </div>
                    {product?.source === "biddeasy" ? (
                        <div className="absolute -right-[25px] top-0 flex flex-col items-center justify-center rounded-l-[7px] bg-[#283A97] px-[9.5px] py-[6.34px]">
                            <BiddLogo />
                        </div>
                    ) : null}
                    <p className="absolute -right-1 top-10 text-left text-sm font-semibold capitalize leading-[19.2px] tracking-[0.12px] text-black opacity-95">
                        {product?.productType}
                    </p>
                </div>
                <div className="flex w-full items-center justify-between gap-2">
                    <Progress
                        value={Number(product?.soldOutTxt ?? 0)}
                        className="h-2 w-[82%] [&>*]:bg-[#f15d2a]"
                    />
                    <p className="text-2xs">
                        {Number(product?.soldOutTxt) > 0
                            ? `${product?.soldOutTxt}% sold`
                            : "0% sold"}
                    </p>
                </div>
            </div>
            <div>
                <p className="text-sm font-semibold leading-[22.4px] tracking-[0.14px]">
                    {product?.productType === "ipo"
                        ? `Close Date: ${moment(new Date(product?.closeDate).getTime()).format("Do MMM'YY")}`
                        : `Maturity Date: ${moment(product?.maturityDate, "MM/DD/YYYY").format("Do MMM'YY")}`}
                </p>
            </div>
            <div className="mb-8 w-full pt-4">
                <div className="mb-4 grid grid-cols-2 gap-6">
                    <div className="h-[56px] w-full">
                        <p className="mb-1 text-left text-sm font-medium leading-[22.4px] tracking-[0.14px] text-neutral-500">
                            Min. Investment
                        </p>
                        <p
                            className="w-full truncate text-left text-[16px] font-semibold leading-[25.6px]"
                            title={formatIndianCurrency(Number(product?.minAmt))}
                        >
                            {formatIndianCurrency(Number(product?.minAmt))}
                        </p>
                    </div>
                    <div className="h-[56px] w-full">
                        <p className="mb-1 text-right text-sm font-medium leading-[22.4px] tracking-[0.14px] text-neutral-500">
                            {product?.productType === "mld" ? "Credit Rating" : "YTM"}
                        </p>
                        <p
                            className="w-full truncate text-right text-[16px] font-semibold leading-[25.6px]"
                            title={product?.productType === "mld" ? product?.rating : `${product?.xirr}%`}
                        >
                            {product?.productType === "mld" ? product?.rating : `${product?.xirr}%`}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                    <div className="h-[56px] w-full">
                        <p className="mb-1 text-left text-sm font-medium leading-[22.4px] tracking-[0.14px] text-neutral-500">
                            {product?.productType === "mld"
                                ? "Returns"
                                : product?.productType === "ipo"
                                  ? "Interest Payout"
                                  : "Principal payout"
                            }
                        </p>
                        <p
                            className="w-full truncate text-left text-[16px] font-semibold leading-[25.6px]"
                            title={product?.productType === "mld"
                                    ? getMldReturns(product?.minReturns, product?.maxReturns)
                                    : product?.productType === "ipo"
                                      ? product?.couponType
                                      : product?.principalRepayment
                            }
                        >
                            {product?.productType === "mld"
                                ? getMldReturns(product?.minReturns, product?.maxReturns)
                                : product?.productType === "ipo"
                                  ? product?.couponType
                                  : product?.principalRepayment
                            }
                        </p>
                    </div>
                    <div className="h-[56px] w-full">
                        <p className="mb-1 text-right text-sm font-medium leading-[22.4px] tracking-[0.14px] text-neutral-500">
                            {product?.productType === "mld"
                                ? "Multiplier"
                                : product?.productType === "ipo"
                                  ? "Principal payout"
                                  : "Interest Payout"
                            }
                        </p>
                        <p
                            className="w-full truncate text-right text-[16px] font-semibold leading-[25.6px]"
                            title={product?.productType === "mld"
                                    ? `${product?.multiplier}x of Nifty 50 Index Returns`
                                    : product?.productType === "ipo"
                                      ? product?.principalRepayment
                                      : product?.couponType
                            }
                        >
                            {product?.productType === "mld"
                                ? `${product?.multiplier}x of Nifty 50 Index Returns`
                                : product?.productType === "ipo"
                                  ? product?.principalRepayment
                                  : product?.couponType
                            }
                        </p>
                    </div>
                </div>
            </div>

            <div className="w-full" id="bonds-product-card-btn-div">
                <RegularButton
                    id="bonds-product-card-btn"
                    noIcon
                    showRightIcon={product?.source === "biddeasy" ? true : false}
                    rightIcon={<ExternalLink />}
                    className="h-[41.71px] !w-full max-w-full !text-[11.73px]"
                    size={ButtonSize.Large}
                    variant={ButtonVariant.PrimaryDefault}
                    label={`Explore${product?.source === "biddeasy" ? " on Bidd" : ""}`}
                    onClick={() => {
                        if (product?.source == "biddeasy") {
                            handleBiddRedirect(product?.productType, product?.product, product?.redirectUrl)
                        } else {
                            redirectToProduct(product?.product || "", product?.productType || "");
                        }
                    }}
                />
            </div>

            <BiddRedirectPopup
                popupRedirectToBidd={popupRedirectToBidd}
                onClose={toggleBiddRedirectPopup}
                currentData={currentData}
            />
        </div>
    );
};

export default BondProductCard;
